import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SponsorBanner from '@/components/SponsorBanner';
import LandingPage from '@/pages/LandingPage';
import AuthPage from '@/pages/AuthPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import RepairReportPage from '@/pages/RepairReportPage';
import AskPage from '@/pages/AskPage';
import SolutionPage from '@/pages/SolutionPage';
import HistoryPage from '@/pages/HistoryPage';
import NameplatePage from '@/pages/NameplatePage';
import AccountPage from '@/pages/AccountPage';
import SponsorAdminPage from '@/pages/SponsorAdminPage';
import AdminBypassPage from '@/pages/AdminBypassPage';
import SubscribePage from '@/pages/SubscribePage';
import SubscribeSuccessPage from '@/pages/SubscribeSuccessPage';
import { Loader2 } from 'lucide-react';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-amber-500" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AppLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-zinc-950">
      <SponsorBanner />
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <AppLayout>
            <LandingPage />
          </AppLayout>
        }
      />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/forgot" element={<ForgotPasswordPage />} />
      <Route path="/auth/reset" element={<ResetPasswordPage />} />
      <Route
        path="/report/:id"
        element={
          <ProtectedRoute>
            <RepairReportPage />
          </ProtectedRoute>
        }
      />
      <Route path="/go" element={<AdminBypassPage />} />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AskPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/solution/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SolutionPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <AppLayout>
              <HistoryPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/nameplate"
        element={
          <ProtectedRoute>
            <AppLayout>
              <NameplatePage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sponsor"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SponsorAdminPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AccountPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/subscribe"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SubscribePage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/subscribe/success"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SubscribeSuccessPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
